import * as _ from 'lodash-es';
import type { Parchment } from 'quill/core';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import SVGIcon from '../../components/SVGIcon';
import caretIcon from '../../icons/non-prefilled/16x16/caret.svg';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';
import GutterButton from '../GutterButton';
import { useQuill } from '../QuillContext';
import { getHeaderCollapse } from '../useHeaderCollapse';
import BasicHeader from './BasicHeader';
import * as styles from './Header.module.scss';
import Scroll from './Scroll';
import useShowEditorToast from '../utils/useShowEditorToast';

interface HeaderButtonsProps {
  blot: Header;
  copyLinkDisabled: boolean;
}

const HeaderButtons = ({ blot, copyLinkDisabled }: HeaderButtonsProps) => {
  const { quill } = useQuill();
  const ref = useRef<HTMLDivElement | null>(null);
  const [lineHeight, setLineHeight] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (ref.current) {
      setLineHeight(getComputedStyle(ref.current).lineHeight);
    }
  }, []);

  const showEditorToast = useShowEditorToast();

  return (
    <div
      ref={ref}
      className={styles.buttonContainer}
      style={{ height: lineHeight }}
    >
      {!copyLinkDisabled && (
        <GutterButton
          className={styles.linkButton}
          onClick={async () => {
            const { location } = window;
            const headerAnchorLink = new URL(location.href);
            headerAnchorLink.hash = blot.getAnchorId();

            await navigator.clipboard.writeText(headerAnchorLink.toString());
            showEditorToast(
              { level: 'success', message: 'Link copied to clipboard' },
              2000,
            );
          }}
          testId="headerLinkButton"
          aria-label="Copy link to header"
        >
          {linkIcon}
        </GutterButton>
      )}
      <GutterButton
        className={styles.collapseButton}
        onClick={() => {
          if (quill) {
            getHeaderCollapse(quill)?.setHeaderCollapsed(blot);
          }
        }}
        testId="headerCollapseButton"
        aria-label="Toggle header collapse"
      >
        <SVGIcon className={styles.collapseButtonIcon}>{caretIcon}</SVGIcon>
      </GutterButton>
    </div>
  );
};

export default class Header extends BasicHeader {
  id: string;
  declare domNode: HTMLHeadingElement;
  protected copyLinkDisabled = false;
  protected override anchorIdEnabled = true;

  constructor(scroll: Parchment.ScrollBlot, domNode: Node) {
    super(scroll, domNode);
    if (domNode instanceof HTMLElement) {
      domNode.classList.add(styles.header);
    }
    this.id = _.uniqueId();
    const uiNode = document.createElement('div');
    uiNode.dataset.noPrint = '';
    this.attachUI(uiNode);
  }

  attach() {
    super.attach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_MOUNT, this);
  }

  detach() {
    super.detach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_UNMOUNT, this);
  }

  renderPortal() {
    return (
      this.uiNode &&
      createPortal(
        <HeaderButtons blot={this} copyLinkDisabled={this.copyLinkDisabled} />,
        this.uiNode,
      )
    );
  }
}
